/**
 * Company Structure Mock Data Generator
 *
 * 该模块提供企业KYC公司结构与关联人士表单的模拟数据生成功能
 * 使用 @faker-js/faker 生成符合表单验证规则的测试数据
 *
 * @module mockDataGenerator.company-structure
 * @description 公司结构与关联人士模拟数据生成器
 */

import {
  CompanyKycIndividualShareholderWealthSourceOptions,
  CompanyLegalPersonShareholderWealthSourceOptions,
  CompanyRelatedPersonTypeOptions,
  CompanyShareholderTypeOptions,
  GenderOptions,
  IdentificationDocumentOptions,
  YesNoOptions,
} from "@/types/enum";
import { faker } from "@faker-js/faker";

/* ============================================================================
 * 类型定义
 * ========================================================================== */

/**
 * 国家数据类型
 */
interface CountryData {
  code2: string;
  cn: string;
  en: string;
  hk: string;
  tel: string;
  url: string;
  priority: number;
}

/**
 * 国家JSON数据结构
 */
interface CountryJsonData {
  code: string;
  msg: string;
  data: CountryData[];
}

// 导入真实的国家数据
import countryAllDataJson from "../../data/country.json";
import countryAllowedDataJson from "../../data/country-allowed.json";
import countryRestrictedDataJson from "../../data/country-restricted.json";

const countryAllData = countryAllDataJson as CountryJsonData;
const countryAllowedData = countryAllowedDataJson as CountryJsonData;
const countryRestrictedData = countryRestrictedDataJson as CountryJsonData;

/**
 * 关联人士数据类型
 */
export interface RelatedPersonData {
  id: string;
  type: CompanyShareholderTypeOptions;
  isShareholdingBeneficiary?: boolean;
  data: Record<string, any>;
}

/**
 * Mock 数据生成配置选项
 */
export interface CompanyStructureMockDataOptions {
  /** 指定人士类型 */
  type?: CompanyShareholderTypeOptions;
  /** 是否为股东/最终实益拥有人 */
  isShareholdingBeneficiary?: boolean;
  /** 自定义关联人士类型（个人）*/
  relatedPersonTypes?: CompanyRelatedPersonTypeOptions[];
  /** 自定义关联人士类型（法人）*/
  corporateRelatedPersonTypes?: CompanyRelatedPersonTypeOptions[];
  /** 自定义种子 */
  seed?: number;
}

/* ============================================================================
 * 常量配置
 * ========================================================================== */

/**
 * 所有国家列表
 */
const ALL_COUNTRIES = countryAllData.data;

/**
 * 允许和受限制的国家列表
 */
const ALLOWED_AND_RESTRICTED_COUNTRIES = [
  ...countryAllowedData.data,
  ...countryRestrictedData.data,
];

/**
 * 国家代码列表
 */
const ALL_COUNTRY_CODES = ALL_COUNTRIES.map((c) => c.code2);
const ALLOWED_AND_RESTRICTED_COUNTRY_CODES =
  ALLOWED_AND_RESTRICTED_COUNTRIES.map((c) => c.code2);

/**
 * 国家代码到电话区号的映射
 */
const COUNTRY_TEL_MAP = new Map(
  ALL_COUNTRIES.map((country) => [country.code2, country.tel])
);

/**
 * 关联人士类型选项
 */
const RELATED_PERSON_TYPE_OPTIONS = Object.values(
  CompanyRelatedPersonTypeOptions
);

/**
 * 性别选项
 */
const GENDER_OPTIONS = Object.values(GenderOptions);

/**
 * 是/否选项
 */
const YES_NO_OPTIONS = Object.values(YesNoOptions);

/**
 * 证件类型选项
 */
const ID_TYPE_OPTIONS = Object.values(IdentificationDocumentOptions);

/**
 * 个人股东财富来源选项
 */
const WEALTH_SOURCE_OPTIONS = Object.values(
  CompanyKycIndividualShareholderWealthSourceOptions
);

/**
 * 法人股东财富来源选项
 */
const CORPORATE_WEALTH_SOURCE_OPTIONS = Object.values(
  CompanyLegalPersonShareholderWealthSourceOptions
);

/* ============================================================================
 * 辅助函数
 * ========================================================================== */

/**
 * 生成英文名字
 */
function generateEnglishName(fakerInstance: typeof faker): string {
  return fakerInstance.person.firstName();
}

/**
 * 生成英文姓氏
 * 规则：只能包含英文字母、数字和空格，长度1-50个字符，必须包含至少一个英文字母，不能以空格开头
 */
function generateEnglishSurname(fakerInstance: typeof faker): string {
  // 使用预定义的常见英文姓氏列表，确保符合验证规则
  const surnames = [
    "Smith",
    "Johnson",
    "Williams",
    "Brown",
    "Jones",
    "Garcia",
    "Miller",
    "Davis",
    "Rodriguez",
    "Martinez",
    "Hernandez",
    "Lopez",
    "Gonzalez",
    "Wilson",
    "Anderson",
    "Thomas",
    "Taylor",
    "Moore",
    "Jackson",
    "Martin",
    "Lee",
    "Thompson",
    "White",
    "Harris",
    "Clark",
    "Lewis",
    "Robinson",
    "Walker",
    "Young",
    "Allen",
    "King",
    "Wright",
    "Scott",
    "Torres",
    "Hill",
    "Green",
    "Adams",
    "Baker",
    "Nelson",
    "Carter",
    "Mitchell",
    "Perez",
    "Roberts",
    "Turner",
    "Phillips",
    "Campbell",
    "Parker",
    "Evans",
    "Edwards",
    "Collins",
    "Stewart",
    "Morris",
    "Rogers",
  ];

  const baseSurname = fakerInstance.helpers.arrayElement(surnames);

  // 有30%的概率添加数字后缀（如 Smith 2, Johnson 3）
  const addNumber = fakerInstance.number.int({ min: 1, max: 10 }) <= 3;

  if (addNumber) {
    const number = fakerInstance.number.int({ min: 2, max: 99 });
    const withNumber = `${baseSurname} ${number}`;
    // 确保不超过50个字符
    return withNumber.length <= 50 ? withNumber : baseSurname;
  }

  return baseSurname;
}

/**
 * 生成中文姓名
 */
function generateChineseName(fakerInstance: typeof faker): string {
  const surnames = ["王", "李", "张", "刘", "陈", "杨", "黄", "赵", "周", "吴"];
  const givenNames = [
    "伟",
    "芳",
    "娜",
    "敏",
    "静",
    "丽",
    "强",
    "磊",
    "军",
    "洋",
  ];

  const surname = fakerInstance.helpers.arrayElement(surnames);
  const givenName1 = fakerInstance.helpers.arrayElement(givenNames);
  const givenName2 = fakerInstance.helpers.arrayElement(givenNames);

  return surname + givenName1 + givenName2;
}

/**
 * 生成电话号码（PhoneNumberInput组件需要的格式）
 */
function generatePhoneNumber(
  fakerInstance: typeof faker,
  country: string
): { phoneCountry: string; phoneNumber: string } {
  const tel = COUNTRY_TEL_MAP.get(country) || "852";
  const phoneCountry = tel;
  const phoneNumber = fakerInstance.string.numeric(8);

  return { phoneCountry, phoneNumber };
}

/**
 * 生成出生日期
 */
function generateBirthDate(fakerInstance: typeof faker): string {
  const birthDate = fakerInstance.date.birthdate({
    min: 18,
    max: 80,
    mode: "age",
  });

  const year = birthDate.getFullYear();
  const month = String(birthDate.getMonth() + 1).padStart(2, "0");
  const day = String(birthDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

/**
 * 生成地址
 */
function generateAddress(fakerInstance: typeof faker, country: string): string {
  if (country === "HKG") {
    const districts = [
      "Central",
      "Admiralty",
      "Wan Chai",
      "Causeway Bay",
      "Tsim Sha Tsui",
    ];
    const district = fakerInstance.helpers.arrayElement(districts);
    const building = fakerInstance.company.name() + " Tower";
    const floor = fakerInstance.number.int({ min: 1, max: 50 });
    const room = fakerInstance.number.int({ min: 1, max: 20 });

    return `Room ${room}, ${floor}/F, ${building}, ${district}, Hong Kong`;
  } else if (country === "CHN") {
    return `中国${fakerInstance.location.city()}${fakerInstance.location.street()}${fakerInstance.number.int({ min: 1, max: 999 })}号`;
  } else {
    return fakerInstance.location.streetAddress({ useFullAddress: true });
  }
}

/**
 * 生成证件号码
 */
function generateIdNumber(
  fakerInstance: typeof faker,
  idType: string,
  country: string
): string {
  if (idType === IdentificationDocumentOptions.PASSPORT) {
    // 护照号码格式
    const prefix = fakerInstance.string.alpha({
      length: fakerInstance.number.int({ min: 1, max: 2 }),
      casing: "upper",
    });
    const numbers = fakerInstance.string.numeric(
      fakerInstance.number.int({ min: 7, max: 9 })
    );
    return prefix + numbers;
  }

  if (idType === IdentificationDocumentOptions.ID_CARD && country === "HKG") {
    // 香港身份证格式（符合alphanumeric规则：仅字母和数字）
    const letter = fakerInstance.string.alpha({ length: 1, casing: "upper" });
    const numbers = fakerInstance.string.numeric(6);
    const checkDigit = fakerInstance.helpers.arrayElement([
      "0",
      "1",
      "2",
      "3",
      "4",
      "5",
      "6",
      "7",
      "8",
      "9",
      "A",
    ]);
    return `${letter}${numbers}${checkDigit}`;
  }

  // 默认：字母数字混合
  return (
    fakerInstance.string.alpha({ length: 2, casing: "upper" }) +
    fakerInstance.string.numeric(8)
  );
}

/**
 * 生成证件有效期（KycDocumentValidityPicker需要的格式）
 */
function generateIdExpiryDate(
  fakerInstance: typeof faker,
  isPermanent?: boolean
): { type: "permanent" | "date"; idExpiryDate?: string } {
  if (isPermanent === undefined) {
    isPermanent = fakerInstance.number.int({ min: 1, max: 10 }) <= 2;
  }

  if (isPermanent) {
    return {
      type: "permanent",
      idExpiryDate: undefined,
    };
  }

  const expiryDate = fakerInstance.date.future({
    years: fakerInstance.number.int({ min: 1, max: 10 }),
  });

  const year = expiryDate.getFullYear();
  const month = String(expiryDate.getMonth() + 1).padStart(2, "0");
  const day = String(expiryDate.getDate()).padStart(2, "0");

  return {
    type: "date",
    idExpiryDate: `${year}-${month}-${day}`,
  };
}

/**
 * 生成税务识别号
 */
function generateTIN(fakerInstance: typeof faker, country: string): string {
  if (country === "HKG") {
    return (
      fakerInstance.string.alpha({ length: 1, casing: "upper" }) +
      fakerInstance.string.numeric(7)
    );
  } else if (country === "CHN") {
    return fakerInstance.string.numeric(18);
  } else {
    return fakerInstance.string.alphanumeric({ length: 10, casing: "upper" });
  }
}

/**
 * 生成税务管辖区域列表（TaxJurisdictionList组件需要的格式）
 */
function generateTaxJurisdictions(
  fakerInstance: typeof faker
): Array<{ tins_country: string; tins_taxId: string }> {
  const count = fakerInstance.number.int({ min: 1, max: 2 });
  const selectedCountries = fakerInstance.helpers.arrayElements(
    ALL_COUNTRY_CODES,
    count
  );

  return selectedCountries.map((country) => ({
    tins_country: country,
    tins_taxId: generateTIN(fakerInstance, country),
  }));
}

/**
 * 生成职位
 */
function generatePosition(
  _fakerInstance: typeof faker,
  relatedPersonTypes: string[]
): string {
  const positions: Record<string, string> = {
    [CompanyRelatedPersonTypeOptions.SHAREHOLDER_BENEFICIAL_OWNER]:
      "Shareholder",
    [CompanyRelatedPersonTypeOptions.DIRECTOR]: "Director",
    [CompanyRelatedPersonTypeOptions.AUTHORIZED_SIGNATORY]:
      "Authorized Signatory",
    [CompanyRelatedPersonTypeOptions.CLIENT_REPRESENTATIVE]:
      "Client Representative",
    [CompanyRelatedPersonTypeOptions.PRINCIPAL]: "Principal",
  };

  const primaryType = relatedPersonTypes[0];
  return positions[primaryType] || "Director";
}

/* ============================================================================
 * 主函数
 * ========================================================================== */

/**
 * 生成个人股东/有关人士数据（含股权信息）
 */
function generatePersonalShareholderData(
  fakerInstance: typeof faker,
  customRelatedPersonTypes?: CompanyRelatedPersonTypeOptions[]
): Record<string, any> {
  const nationality = fakerInstance.helpers.arrayElement(ALL_COUNTRY_CODES);
  const residenceCountry = fakerInstance.helpers.arrayElement(
    ALLOWED_AND_RESTRICTED_COUNTRY_CODES
  );
  const phone = generatePhoneNumber(fakerInstance, residenceCountry);

  // 关联人士类型（默认包含股东，或使用自定义类型）
  const relatedPersonTypes =
    customRelatedPersonTypes ||
    ([
      CompanyRelatedPersonTypeOptions.SHAREHOLDER_BENEFICIAL_OWNER,
    ] as CompanyRelatedPersonTypeOptions[]);

  // 证件信息
  const idIssuingCountry =
    fakerInstance.helpers.arrayElement(ALL_COUNTRY_CODES);
  const idType = fakerInstance.helpers.arrayElement(ID_TYPE_OPTIONS);
  const idExpiryDate = generateIdExpiryDate(fakerInstance);

  // 财富来源（选择1-3个）
  const wealthSource = fakerInstance.helpers.arrayElements(
    WEALTH_SOURCE_OPTIONS,
    fakerInstance.number.int({ min: 1, max: 3 })
  );

  // 财富来源国家
  const initialWealthSourceCountry =
    fakerInstance.helpers.arrayElement(ALL_COUNTRY_CODES);
  const continuousWealthSourceCountry =
    fakerInstance.helpers.arrayElement(ALL_COUNTRY_CODES);

  // 如果财富来源选择了"其他"，生成详细说明
  const otherWealthSourceRemark = wealthSource.includes(
    CompanyKycIndividualShareholderWealthSourceOptions.OTHER
  )
    ? fakerInstance.helpers.arrayElement([
        "历年累计盈余公积",
        "资本公积转增",
        "品牌溢价收益",
        "技术许可费收入",
        "特许经营权收益",
        "商誉评估增值",
      ])
    : undefined;

  return {
    relatedPersonTypes,
    // 基本信息
    firstName: generateEnglishName(fakerInstance),
    middleName: generateEnglishName(fakerInstance), // 总是生成中间名
    lastName: generateEnglishSurname(fakerInstance),
    chineseName: generateChineseName(fakerInstance), // 总是生成中文姓名
    gender: fakerInstance.helpers.arrayElement(GENDER_OPTIONS),
    birthDate: generateBirthDate(fakerInstance),
    phoneNumber: phone,
    nationality,
    residenceCountry,
    residentialAddress: generateAddress(fakerInstance, residenceCountry),
    position: generatePosition(fakerInstance, relatedPersonTypes),
    // 证件信息
    idIssuingCountry,
    idType,
    idNumber: generateIdNumber(fakerInstance, idType, idIssuingCountry),
    idValidityType: idExpiryDate,
    // 财务信息
    wealthSource,
    otherWealthSourceRemark,
    initialWealthSourceCountry,
    continuousWealthSourceCountry,
    shareholdingRatio: fakerInstance.number
      .int({ min: 1, max: 100 })
      .toString(),
    isActualController: fakerInstance.helpers.arrayElement(YES_NO_OPTIONS),
    isFinexController: fakerInstance.helpers.arrayElement(YES_NO_OPTIONS),
    hasRelatedAccounts: fakerInstance.helpers.arrayElement(YES_NO_OPTIONS),
    // 纳税信息
    tins: generateTaxJurisdictions(fakerInstance),
  };
}

/**
 * 生成个人有关人士数据（不含股权信息）
 */
function generatePersonalNonShareholderData(
  fakerInstance: typeof faker,
  customRelatedPersonTypes?: CompanyRelatedPersonTypeOptions[]
): Record<string, any> {
  const nationality = fakerInstance.helpers.arrayElement(ALL_COUNTRY_CODES);
  const residenceCountry = fakerInstance.helpers.arrayElement(
    ALLOWED_AND_RESTRICTED_COUNTRY_CODES
  );
  const phone = generatePhoneNumber(fakerInstance, residenceCountry);

  // 使用自定义类型或随机选择1-3个关联人士类型（排除股东类型）
  let selectedTypes: CompanyRelatedPersonTypeOptions[];
  if (customRelatedPersonTypes) {
    selectedTypes = customRelatedPersonTypes;
  } else {
    const nonShareholderTypes = RELATED_PERSON_TYPE_OPTIONS.filter(
      (type) =>
        type !== CompanyRelatedPersonTypeOptions.SHAREHOLDER_BENEFICIAL_OWNER
    );
    selectedTypes = fakerInstance.helpers.arrayElements(
      nonShareholderTypes,
      fakerInstance.number.int({ min: 1, max: 3 })
    );
  }

  // 证件信息
  const idIssuingCountry =
    fakerInstance.helpers.arrayElement(ALL_COUNTRY_CODES);
  const idType = fakerInstance.helpers.arrayElement(ID_TYPE_OPTIONS);
  const idExpiryDate = generateIdExpiryDate(fakerInstance);

  return {
    relatedPersonTypes: selectedTypes,
    // 基本信息
    firstName: generateEnglishName(fakerInstance),
    middleName: generateEnglishName(fakerInstance), // 总是生成中间名
    lastName: generateEnglishSurname(fakerInstance),
    chineseName: generateChineseName(fakerInstance), // 总是生成中文姓名
    gender: fakerInstance.helpers.arrayElement(GENDER_OPTIONS),
    birthDate: generateBirthDate(fakerInstance),
    phoneNumber: phone,
    nationality,
    residenceCountry,
    residentialAddress: generateAddress(fakerInstance, residenceCountry),
    position: generatePosition(fakerInstance, selectedTypes),
    // 证件信息
    idIssuingCountry,
    idType,
    idNumber: generateIdNumber(fakerInstance, idType, idIssuingCountry),
    idValidityType: idExpiryDate,
  };
}

/**
 * 生成法人股东/有关人士数据
 */
function generateCorporateShareholderData(
  fakerInstance: typeof faker,
  customRelatedPersonTypes?: CompanyRelatedPersonTypeOptions[]
): Record<string, any> {
  const registrationCountry = fakerInstance.helpers.arrayElement(
    ALLOWED_AND_RESTRICTED_COUNTRY_CODES
  );
  const phone = generatePhoneNumber(fakerInstance, registrationCountry);

  // 关联人士类型（默认为股东）
  const corporateCompanyRelatedPersonTypes = customRelatedPersonTypes || [
    CompanyRelatedPersonTypeOptions.SHAREHOLDER_BENEFICIAL_OWNER,
  ];

  // 财富来源（选择1-3个）
  const corporateWealthSource = fakerInstance.helpers.arrayElements(
    CORPORATE_WEALTH_SOURCE_OPTIONS,
    fakerInstance.number.int({ min: 1, max: 3 })
  );

  // 财富来源国家
  const corporateInitialWealthSourceCountry =
    fakerInstance.helpers.arrayElement(ALL_COUNTRY_CODES);
  const corporateContinuousWealthSourceCountry =
    fakerInstance.helpers.arrayElement(ALL_COUNTRY_CODES);

  // 生成中文公司名称（总是生成）
  const corporateNameZh = `${fakerInstance.helpers.arrayElement(["香港", "深圳", "上海", "北京", "广州"])}${fakerInstance.helpers.arrayElement(["新世纪", "华泰", "恒大", "万通", "鼎盛"])}${fakerInstance.helpers.arrayElement(["科技", "贸易", "金融", "实业"])}有限公司`;

  // 生成符合规则的企业全称：只能包含英文字母、数字和空格，长度1-50个字符，必须包含至少一个英文字母，不能以空格开头
  const generateCorporateName = () => {
    const companyTypes = [
      "Corporation",
      "Limited",
      "Inc",
      "LLC",
      "Group",
      "Holdings",
      "Enterprises",
      "International",
      "Global",
    ];
    const companyPrefixes = [
      "Tech",
      "Future",
      "Digital",
      "Smart",
      "Global",
      "United",
      "Pacific",
      "Asia",
      "New",
      "Prime",
    ];
    const companyMiddles = [
      "Vision",
      "Star",
      "Power",
      "Solutions",
      "Systems",
      "Dynamics",
      "Capital",
      "Ventures",
      "Partners",
      "Industries",
    ];

    const prefix = fakerInstance.helpers.arrayElement(companyPrefixes);
    const middle = fakerInstance.helpers.maybe(
      () => fakerInstance.helpers.arrayElement(companyMiddles),
      { probability: 0.6 }
    );
    const type = fakerInstance.helpers.arrayElement(companyTypes);
    const hasNumber = fakerInstance.helpers.maybe(
      () => fakerInstance.number.int({ min: 1, max: 999 }).toString(),
      { probability: 0.3 }
    );

    let name = prefix;
    if (middle) name += ` ${middle}`;
    if (hasNumber) name += ` ${hasNumber}`;
    name += ` ${type}`;

    // 确保长度不超过50个字符
    if (name.length > 50) {
      name = `${prefix} ${type}`;
    }

    return name;
  };

  return {
    corporateCompanyRelatedPersonTypes,
    // 基本信息
    corporateName: generateCorporateName(),
    corporateNameZh, // 总是生成中文公司名称
    registrationCountry,
    officeAddress: generateAddress(fakerInstance, registrationCountry),
    corporatePhoneNumber: phone,
    // 财务信息
    corporateWealthSource,
    corporateInitialWealthSourceCountry,
    corporateContinuousWealthSourceCountry,
    corporateShareholdingRatio: fakerInstance.number
      .int({ min: 1, max: 100 })
      .toString(),
  };
}

/**
 * 生成单个关联人士数据
 */
export function generateRelatedPersonData(
  options: CompanyStructureMockDataOptions = {}
): RelatedPersonData {
  const {
    type = CompanyShareholderTypeOptions.INDIVIDUAL,
    isShareholdingBeneficiary = false,
    relatedPersonTypes,
    corporateRelatedPersonTypes,
    seed,
  } = options;

  // 创建 faker 实例
  const fakerInstance = seed !== undefined ? faker : faker;
  if (seed !== undefined) {
    fakerInstance.seed(seed);
  }

  const id = `person_${Date.now()}_${fakerInstance.string.alphanumeric(6)}`;

  let data: Record<string, any>;

  if (type === CompanyShareholderTypeOptions.CORPORATE) {
    data = generateCorporateShareholderData(
      fakerInstance,
      corporateRelatedPersonTypes
    );
  } else if (isShareholdingBeneficiary || relatedPersonTypes) {
    data = generatePersonalShareholderData(fakerInstance, relatedPersonTypes);
  } else {
    data = generatePersonalNonShareholderData(
      fakerInstance,
      relatedPersonTypes
    );
  }

  return {
    id,
    type,
    isShareholdingBeneficiary,
    data,
  };
}

/**
 * 生成多个关联人士数据
 */
export function generateRelatedPersonsBatch(
  count: number,
  options: CompanyStructureMockDataOptions = {}
): RelatedPersonData[] {
  return Array.from({ length: count }, (_, index) => {
    const itemOptions =
      options.seed !== undefined
        ? { ...options, seed: options.seed + index }
        : options;

    return generateRelatedPersonData(itemOptions);
  });
}

/**
 * 预设场景
 */
export const companyStructurePresets = {
  /**
   * 个人股东
   */
  individualShareholder: (): RelatedPersonData =>
    generateRelatedPersonData({
      type: CompanyShareholderTypeOptions.INDIVIDUAL,
      isShareholdingBeneficiary: true,
    }),

  /**
   * 个人董事
   */
  individualDirector: (): RelatedPersonData =>
    generateRelatedPersonData({
      type: CompanyShareholderTypeOptions.INDIVIDUAL,
      isShareholdingBeneficiary: false,
    }),

  /**
   * 法人股东
   */
  corporateShareholder: (): RelatedPersonData =>
    generateRelatedPersonData({
      type: CompanyShareholderTypeOptions.CORPORATE,
    }),

  /**
   * 混合类型（2个个人 + 1个法人）
   */
  mixedStructure: (): RelatedPersonData[] => [
    companyStructurePresets.individualShareholder(),
    companyStructurePresets.individualDirector(),
    companyStructurePresets.corporateShareholder(),
  ],
};

/* ============================================================================
 * 导出
 * ========================================================================== */

export default {
  generateRelatedPersonData,
  generateRelatedPersonsBatch,
  companyStructurePresets,
};
